import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { GenericListType, LoadMoreQuery } from '../../common/components/lists/generic-list.component';
import { AppState } from '../../store';
import {
  closeChildJobDetailsModal,
  closeJobDetailsModal,
  closeJobsModal,
  getJobsList,
  loadMoreJobsList,
  openChildJobDetailsModal,
  openJobDetailsModal,
  openJobsModal,
  searchJobsList,
} from '../store/jobs.actions';
import {
  selectJobs,
  selectJobIsDetailsModalOpenFlag,
  selectJobsIsModalOpenFlag,
  selectJobsLoadingFlag,
  selectChildJobIsDetailsModalOpenFlag,
  selectAllJobsLoadedFlag,
} from '../store/jobs.selectors';
import { AssistedSearchJobsConfiguratorService } from '../../common/services/assisted-search/assisted-search-jobs-configurator.service';
import { SearchConfig } from '../../common/services/assisted-search/search-config.model';
import { Job } from '../jobs.models';
import { selectClustersIsModalOpenFlag } from '../../clusters/store/clusters.selectors';
import { closeClustersModal } from '../../clusters/store/clusters.actions';
import { Cluster, ClusterTypes } from '../../clusters/clusters.models';
import { QueryParamsJobsList } from '../../common/helper/query-params-generic-list.helper';

@Component({
  selector: 'jobs-list',
  template: `
    <generic-list
      [type]="type"
      [items]="jobs$ | async"
      [isLoading]="jobsLoading$ | async"
      [hasNewButton]="true"
      (createClick)="openJobCreateModal()"
      [newBtnText]="'job.generic-object.buttons.new' | translate"
      [emptyTitle]="'job.generic-object.empty.title' | translate"
      [emptyDescription]="'job.generic-object.empty.description' | translate"
      [allItemsLoaded]="areAllJobsLoaded$ | async"
      [limit]="queryParams.limit"
      (loadMore)="loadMoreJobs($event)"
      [searchPlaceholder]="'job.generic-object.placeholders.search' | translate"
      [searchConfig]="searchConfig"
      [searchQueryFromUrl]="searchQueryFromUrl"
      [hasSearch]="true"
      (searchSubmit)="onSearchSubmit($event)"
    >
      <ng-template templateName="listItem" let-item>
        <job-list-item [item]="item" (openJobDetails)="openJobDetailsModal($event)"></job-list-item>
      </ng-template>
      <ng-template templateName="headers">
        <div class="generic-list-headers jobs">
          <div class="job">{{ 'generic-list.jobs.id' | translate }}</div>
          <div class="package">{{ 'generic-list.jobs.package' | translate }}</div>
          <div class="cluster">{{ 'generic-list.jobs.cluster' | translate }}</div>
          <div class="runtime">{{ 'generic-list.jobs.runtime' | translate }}</div>
          <div class="status">{{ 'generic-list.jobs.status' | translate }}</div>
        </div>
      </ng-template>
    </generic-list>
    <xp-modal
      [isOpen]="isClusterModalOpen$ | async"
      [closeButtonText]="'cluster.generic-object.buttons.close' | translate"
      [saveButtonText]="'cluster.generic-object.buttons.save' | translate"
      [titleText]="'cluster.generic-object.title' | translate"
      (close)="onClusterModalClose()"
    >
      <ng-template>
        <cluster-form
          [item]="clusterFormItem"
          [isInJobsView]="true"
          (clusterCreated)="onClusterCreated($event)"
        ></cluster-form>
      </ng-template>
    </xp-modal>
    <xp-modal
      [isOpen]="isJobModalOpen$ | async"
      [closeButtonText]="'job.generic-object.buttons.close' | translate"
      [saveButtonText]="'job.generic-object.buttons.save' | translate"
      [titleText]="'job.generic-object.title' | translate"
      (close)="onJobModalClose()"
    >
      <ng-template>
        <job-form [item]="formItem" [createdCluster]="createdCluster"></job-form>
      </ng-template>
    </xp-modal>
    <xp-modal
      [isOpen]="isJobDetailsModalOpen$ | async"
      [closeButtonText]="'job.generic-list.item.modals.output.close' | translate"
      [saveButtonText]="'job.generic-object.buttons.save' | translate"
      [titleText]="'job.generic-object.title' | translate"
      (close)="onJobDetailsModalClose()"
    >
      <ng-template>
        <job-details [item]="jobDetailsItem" (childOpen)="openChildJobDetailsModal($event)"></job-details>
      </ng-template>
    </xp-modal>
    <xp-modal
      [isOpen]="isChildJobDetailsModalOpen$ | async"
      [closeButtonText]="'job.generic-list.item.modals.output.close_details' | translate"
      [saveButtonText]="'job.generic-object.buttons.save' | translate"
      [titleText]="'job.generic-object.title' | translate"
      [isBack]="true"
      (close)="onChildJobDetailsModalClose()"
    >
      <ng-template>
        <job-details [item]="childJobDetailsItem"></job-details>
      </ng-template>
    </xp-modal>
  `,
})
export class JobsListComponent implements AfterViewInit, OnInit, OnDestroy {
  type = GenericListType.jobs;
  jobsLoading$ = this.store.select(selectJobsLoadingFlag);
  isJobModalOpen$ = this.store.select(selectJobsIsModalOpenFlag);
  isJobDetailsModalOpen$ = this.store.select(selectJobIsDetailsModalOpenFlag);
  isClusterModalOpen$ = this.store.select(selectClustersIsModalOpenFlag);
  isChildJobDetailsModalOpen$ = this.store.select(selectChildJobIsDetailsModalOpenFlag);
  areAllJobsLoaded$ = this.store.select(selectAllJobsLoadedFlag);
  jobs$ = this.store.select(selectJobs);
  queryParams = QueryParamsJobsList;
  formItem: Partial<Job> = {};
  searchQueryFromUrl = '';
  searchConfig: SearchConfig = {};
  jobDetailsItem: Partial<Job> = {};
  childJobDetailsItem: Partial<Job> = {};
  jobSubscription: Subscription;
  searchSubscription: Subscription;
  clusterFormItem: Partial<Cluster> = {
    name: '',
    type: ClusterTypes.sandbox,
    nodes: 1,
    terminate_on_idle: true,
    time_to_idle: 600,
    reuse_cluster_strategy: 'any',
  };
  createdCluster: Cluster = null;

  constructor(
    @Inject('$rootScope') private $rootScope: any,
    @Inject('$stateParams') private $stateParams: any,
    private store: Store<AppState>,
    private assistedSearchJobsConfigurator: AssistedSearchJobsConfiguratorService,
  ) {}

  ngOnInit() {
    const query = this.$rootScope.getQueryParams('query');

    this.searchQueryFromUrl = query || '';

    this.searchSubscription = this.assistedSearchJobsConfigurator.getConfig$().subscribe((config) => {
      this.searchConfig = config;
    });

    if (!query) {
      this.store.dispatch(getJobsList({ params: this.queryParams }));
    }
  }

  ngAfterViewInit() {
    if (this.$rootScope.stateIs('app.account.jobs.new')) {
      this.openJobCreateModal();
    }

    if (this.$rootScope.stateIncludes('app.account.jobs.item')) {
      this.openJobDetailsModal({ id: this.$stateParams.id });
    }

    if (this.$rootScope.stateIncludes('app.account.jobs.item.children')) {
      this.openJobDetailsModal({ id: this.$stateParams.id });
      this.openChildJobDetailsModal({ id: this.$stateParams.child_id });
    }
  }

  openJobCreateModal(): void {
    this.$rootScope.stateGo('app.account.jobs.new');
    this.store.dispatch(openJobsModal());
  }

  openJobDetailsModal(job: Partial<Job>): void {
    this.jobDetailsItem = job;
    this.$rootScope.stateGo('app.account.jobs.item', { id: job.id });
    this.store.dispatch(openJobDetailsModal());
  }

  openChildJobDetailsModal(job: Partial<Job>): void {
    this.childJobDetailsItem = job;
    this.$rootScope.stateGo('app.account.jobs.item.children', { id: this.jobDetailsItem.id, child_id: job.id });
    this.store.dispatch(openChildJobDetailsModal());
  }

  onJobModalClose(): void {
    this.$rootScope.stateGo('app.account.jobs');
    this.store.dispatch(closeJobsModal());
  }

  onClusterCreated(cluster: Cluster): void {
    this.createdCluster = cluster;
  }

  onClusterModalClose(): void {
    this.store.dispatch(closeClustersModal());
  }

  onJobDetailsModalClose(): void {
    this.$rootScope.stateGo('app.account.jobs');
    this.store.dispatch(closeJobDetailsModal());
  }

  onChildJobDetailsModalClose(): void {
    this.$rootScope.stateGo('app.account.jobs.item', { id: this.jobDetailsItem.id });
    this.store.dispatch(closeChildJobDetailsModal());
  }

  loadMoreJobs(query: LoadMoreQuery) {
    const params: any = {
      ...this.queryParams,
      ...query,
    };
    this.store.dispatch(loadMoreJobsList({ params }));
  }

  onSearchSubmit(query: string) {
    const params = { ...this.queryParams, q: query };
    this.queryParams = params;

    if (!query) {
      this.store.dispatch(getJobsList({ params: this.queryParams }));
    } else {
      this.store.dispatch(searchJobsList({ params }));
    }
  }

  ngOnDestroy() {
    if (this.jobSubscription) {
      this.jobSubscription.unsubscribe();
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }
}

((window as any).xplenty as angular.IModule).directive(
  'jobsList',
  downgradeComponent({
    component: JobsListComponent,
  }),
);
